<template>
  <div class="section">
    <div class="container">
      <h1 class="title">Mapping Management</h1>

      <div v-if="toast.message" :class="['notification', toast.type === 'success' ? 'is-success' : 'is-danger']">
        {{ toast.message }}
      </div>

      <button class="button is-primary mb-4" @click="openModal()">+ Create Mapping</button>

      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th>Model Type</th>
            <th>Field</th>
            <th>Mapping Type</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="mapping in mappings" :key="mapping.id">
            <td>{{ mapping.model_type }}</td>
            <td>{{ mapping.field }}</td>
            <td>{{ mapping.mapping_schema?.type }}</td>
            <td>
              <button class="button is-small is-info mr-2" @click="openModal(mapping)">Edit</button>
              <button class="button is-small is-danger" @click="deleteMapping(mapping.id)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Modal -->
      <div class="modal" :class="{ 'is-active': showModal }">
        <div class="modal-background" @click="closeModal"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ isEditing ? 'Edit Mapping' : 'Create Mapping' }}</p>
            <button class="delete" aria-label="close" @click="closeModal"></button>
          </header>

          <section class="modal-card-body">
            <!-- Model Type -->
            <div class="field">
              <label class="label">Model Type</label>
              <div class="control">
                <div class="select" :class="{ 'is-danger': errorFields.includes('model_type') }">
                  <select v-model="form.model_type">
                    <option value="Invoice">Invoice</option>
                    <option value="Item">Item</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Field Name -->
            <div class="field">
              <label class="label">Field Name</label>
              <div class="control">
                <input class="input" v-model="form.field" type="text" placeholder="Field name" :class="{ 'is-danger': errorFields.includes('field') }" />
              </div>
            </div>

            <!-- Mapping Type -->
            <div class="field">
              <label class="label">Mapping Type</label>
              <div class="control">
                <div class="select" :class="{ 'is-danger': errorFields.includes('mapping_type') }">
                  <select v-model="form.mapping_type">
                    <option value="conditional_lookup">Conditional Lookup</option>
                    <option value="conditional_set">Conditional Set</option>
                    <option value="hardcode">Hardcode</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Conditions -->
            <div v-if="['conditional_lookup', 'conditional_set'].includes(form.mapping_type)" class="field">
              <label class="label">Conditions</label>
              <div v-for="(condition, index) in form.conditions" :key="index" class="box">
                <div class="field">
                  <label class="label">Condition Field</label>
                  <input class="input" v-model="condition.field" placeholder="Field to check" />
                </div>
                <div class="field">
                  <label class="label">Value</label>
                  <input class="input" v-model="condition.value" placeholder="Expected value" />
                </div>
                <div class="field">
                  <label class="label">Model Type</label>
                  <div class="select">
                    <select v-model="condition.model_type">
                      <option value="Invoice">Invoice</option>
                      <option value="Item">Item</option>
                    </select>
                  </div>
                </div>
                <button class="button is-small is-danger mt-2" @click="removeCondition(index)">Remove Condition</button>
              </div>
              <button class="button is-small is-primary mt-2" @click="addCondition()">+ Add Condition</button>
            </div>

            <!-- Lookup Table -->
            <div v-if="form.mapping_type === 'conditional_lookup'" class="field">
              <label class="label">Lookup Table</label>
              <div v-for="(pair, index) in form.lookup_table" :key="index" class="box">
                <div class="field">
                  <input class="input" v-model="pair.key" placeholder="Key" />
                </div>
                <div class="field">
                  <input class="input" v-model="pair.value" placeholder="Value" />
                </div>
                <button class="button is-small is-danger mt-2" @click="removeLookup(index)">Remove Lookup</button>
              </div>
              <button class="button is-small is-primary mt-2" @click="addLookup()">+ Add Lookup</button>
            </div>

            <!-- Set Value (for conditional_set) -->
            <div v-if="form.mapping_type === 'conditional_set'" class="field">
              <label class="label">Set Value</label>
              <input class="input" v-model="form.set_value" placeholder="Value to set if conditions matched" />
            </div>

            <!-- Default Value -->
            <div class="field">
              <label class="label">Default Value</label>
              <input class="input" v-model="form.default_value" placeholder="Default value" />
            </div>

            <!-- JSON Preview -->
            <div class="field">
              <label class="label">Generated Mapping Schema</label>
              <pre class="box" style="white-space: pre-wrap;">{{ generatedSchema }}</pre>
            </div>

          </section>

          <footer class="modal-card-foot">
            <button class="button is-success" :disabled="isSaving" @click="saveMapping()">{{ isSaving ? 'Saving...' : 'Save' }}</button>
            <button class="button" @click="closeModal">Cancel</button>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

const mappings = ref([])
const showModal = ref(false)
const isEditing = ref(false)
const isSaving = ref(false)
const errorFields = ref([])
const toast = ref({ message: '', type: '' })

const form = ref({
  id: null,
  model_type: 'Invoice',
  field: '',
  mapping_type: 'conditional_lookup',
  conditions: [],
  lookup_table: [],
  set_value: '',
  default_value: ''
})

const fetchMappings = async () => {
  const res = await axios.get('/api/v1/inputvalidation/data-mapping/')
  mappings.value = res.data
}

const openModal = (mapping = null) => {
  if (mapping) {
    isEditing.value = true
    const schema = mapping.mapping_schema || {}
    form.value = {
      id: mapping.id,
      model_type: mapping.model_type,
      field: mapping.field,
      mapping_type: schema.type || '',
      conditions: schema.conditions || [],
      lookup_table: schema.lookup_table ? Object.entries(schema.lookup_table).map(([key, value]) => ({ key, value })) : [],
      set_value: schema.set_value || '',
      default_value: schema.default || ''
    }
  } else {
    isEditing.value = false
    form.value = {
      id: null,
      model_type: 'Invoice',
      field: '',
      mapping_type: 'conditional_lookup',
      conditions: [],
      lookup_table: [],
      set_value: '',
      default_value: ''
    }
  }
  errorFields.value = []
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}

const addCondition = () => {
  form.value.conditions.push({ field: '', value: '', model_type: 'Invoice' })
}

const removeCondition = (index) => {
  form.value.conditions.splice(index, 1)
}

const addLookup = () => {
  form.value.lookup_table.push({ key: '', value: '' })
}

const removeLookup = (index) => {
  form.value.lookup_table.splice(index, 1)
}

const generatedSchema = computed(() => {
  if (form.value.mapping_type === 'hardcode') {
    return JSON.stringify({ type: 'hardcoded', value: form.value.default_value }, null, 2)
  }
  if (form.value.mapping_type === 'conditional_set') {
    return JSON.stringify({
      type: 'conditional_set',
      conditions: form.value.conditions,
      set_value: form.value.set_value,
      default: form.value.default_value
    }, null, 2)
  }
  if (form.value.mapping_type === 'conditional_lookup') {
    const lookup = {}
    form.value.lookup_table.forEach(pair => {
      if (pair.key) lookup[pair.key] = pair.value
    })
    return JSON.stringify({
      type: 'conditional_lookup',
      conditions: form.value.conditions,
      lookup_table: lookup,
      default: form.value.default_value
    }, null, 2)
  }
  // ✅ If no mapping_type matched, still return an empty JSON
  return '{}'
})

const validateForm = () => {
  errorFields.value = []
  if (!form.value.model_type) errorFields.value.push('model_type')
  if (!form.value.field) errorFields.value.push('field')
  if (!form.value.mapping_type) errorFields.value.push('mapping_type')
  return errorFields.value.length === 0
}

const showToast = (message, type = 'success') => {
  toast.value = { message, type }
  setTimeout(() => { toast.value = { message: '', type: '' } }, 3000)
}

const saveMapping = async () => {
  if (!validateForm()) {
    showToast('Please fill all required fields.', 'error')
    return
  }
  isSaving.value = true
  const payload = {
    model_type: form.value.model_type,
    field: form.value.field,
    mapping_schema: JSON.parse(generatedSchema.value)
  }
  try {
    if (isEditing.value) {
      await axios.put(`/api/v1/inputvalidation/data-mapping/${form.value.id}/`, payload)
    } else {
      await axios.post('/api/v1/inputvalidation/data-mapping/', payload)
    }
    showToast('Mapping saved successfully!')
    await fetchMappings()
    closeModal()
  } catch (e) {
    console.error(e)
    showToast('Failed to save mapping.', 'error')
  } finally {
    isSaving.value = false
  }
}

const deleteMapping = async (id) => {
  if (confirm('Are you sure you want to delete this mapping?')) {
    try {
      await axios.delete(`/api/v1/inputvalidation/data-mapping/${id}/`)
      showToast('Mapping deleted successfully!')
      await fetchMappings()
    } catch (e) {
      console.error(e)
      showToast('Failed to delete mapping.', 'error')
    }
  }
}

onMounted(() => {
  fetchMappings()
})
</script>

<style scoped>
.modal-card {
  width: 800px;
  max-width: 90%;
}
.notification {
  margin-bottom: 1rem;
}
pre {
  background: #f6f8fa;
  padding: 1rem;
  border-radius: 4px;
}
.is-danger input,
.is-danger select {
  border-color: red;
}
</style>
